import React, { useId, useState } from 'react';

import { Paper } from '@mui/material';

import { formatInteger, getNiceMax } from '../utils/numberUtils';
import './lineChart.css';

const DENSE_POINT_COUNT = 90;

const SERIES_COLORS = ['var(--chart-series-1)', 'var(--chart-series-2)', 'var(--chart-series-3)', 'var(--chart-series-4)', 'var(--chart-series-5)', 'var(--chart-series-6)'];

const getRowValue = (row, valueKey) => {
    const value = Number(row[valueKey]);
    if (Number.isNaN(value)) {
        throw new Error(`Valor inválido recebido para o gráfico de linha: ${row[valueKey]}`);
    }

    return value;
};

const LineChart = ({
    ariaLabel,
    eyebrow,
    icon: Icon,
    maxXTicks,
    pointAriaLabel,
    pointKey,
    peakText,
    rows,
    series,
    title,
    tooltipValue,
    valueKey,
    xTickLabel,
    yTickLabel = (tick) => formatInteger(Math.round(tick))
}) => {
    const [activePoint, setActivePoint] = useState(null);
    const gradientId = useId().replace(/:/g, '');
    const chartSeries = (series || [{ key: valueKey, peakText, tooltipValue }]).map((serie, index) => ({
        ...serie,
        color: serie.color || SERIES_COLORS[index % SERIES_COLORS.length]
    }));
    const isMultiSeries = chartSeries.length > 1;
    const maxValue = Math.max(
        ...rows.flatMap((row) => chartSeries.map((serie) => getRowValue(row, serie.key))),
        1
    );
    const axisMax = getNiceMax(maxValue);
    const width = 1000;
    const height = 250;
    const padding = {
        top: 18,
        right: 6,
        bottom: 28,
        left: 44
    };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const divisor = Math.max(rows.length - 1, 1);
    const points = rows.map((row, index) => {
        const x = padding.left + (index / divisor) * chartWidth;
        const values = chartSeries.map((serie) => {
            const value = getRowValue(row, serie.key);

            return {
                key: serie.key,
                value,
                y: padding.top + chartHeight - (value / axisMax) * chartHeight
            };
        });

        return { ...row, x, values, value: values[0].value, y: values[0].y };
    });
    const yTicks = [axisMax, axisMax / 2, 0];
    const xTickStep = maxXTicks ? Math.max(Math.ceil(points.length / maxXTicks), 1) : 5;
    const xTicks = points.filter((point, index) => index % xTickStep === 0 || index === points.length - 1);
    const hasVisiblePoints = points.length <= DENSE_POINT_COUNT;
    const hasCircleHitArea = hasVisiblePoints && !isMultiSeries;
    const hitAreaWidth = Math.max(chartWidth / divisor, 3);
    const seriesLines = chartSeries.map((serie, serieIndex) => ({
        ...serie,
        linePoints: points.map((point) => `${point.x},${point.values[serieIndex].y}`).join(' '),
        peak: points.reduce(
            (currentPeak, point) => point.values[serieIndex].value > currentPeak.values[serieIndex].value
                ? point
                : currentPeak,
            points[0]
        )
    }));
    const areaPoints = points.length
        ? `${padding.left},${height - padding.bottom} ${seriesLines[0].linePoints} ${width - padding.right},${height - padding.bottom}`
        : '';
    const activeTooltipStyle = activePoint
        ? {
            left: `${(activePoint.x / width) * 100}%`,
            top: `${(Math.min(...activePoint.values.map((seriesValue) => seriesValue.y)) / height) * 100}%`
        }
        : undefined;
    const isActivePoint = (point) => Boolean(activePoint) && pointKey(activePoint) === pointKey(point);

    return (
        <Paper className='standard-line-chart-panel'>
            <div className='standard-line-chart-heading'>
                <div>
                    <p>{eyebrow}</p>
                    <h2>{title}</h2>
                </div>
                {Icon && <Icon />}
            </div>

            {isMultiSeries && (
                <div className='standard-line-chart-legend'>
                    {chartSeries.map((serie) => (
                        <span key={serie.key} className={serie.dashed ? 'is-dashed' : undefined} style={{ '--series-color': serie.color }}>
                            <i />
                            {serie.label}
                        </span>
                    ))}
                </div>
            )}

            <div className='standard-line-chart' onMouseLeave={() => setActivePoint(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} role='img' aria-label={ariaLabel}>
                    <defs>
                        <linearGradient id={gradientId} x1='0' x2='0' y1='0' y2='1'>
                            <stop offset='0%' stopColor='var(--chart-1)' stopOpacity='.12' />
                            <stop offset='100%' stopColor='var(--chart-1)' stopOpacity='0' />
                        </linearGradient>
                    </defs>
                    {yTicks.map((tick) => {
                        const y = padding.top + chartHeight - (tick / axisMax) * chartHeight;

                        return (
                            <g key={tick}>
                                <line className='standard-line-chart-grid-line' x1={padding.left} x2={width - padding.right} y1={y} y2={y} />
                                <text className='standard-line-chart-axis-label' x={padding.left - 12} y={y + 5}>
                                    {yTickLabel(tick)}
                                </text>
                            </g>
                        );
                    })}
                    <line className='standard-line-chart-axis' x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} />
                    <line className='standard-line-chart-axis' x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} />
                    {xTicks.map((point) => (
                        <g key={`tick-${pointKey(point)}`}>
                            <line className='standard-line-chart-x-tick' x1={point.x} x2={point.x} y1={height - padding.bottom} y2={height - padding.bottom + 5} />
                            <text className='standard-line-chart-x-label' x={point.x} y={height - 6}>
                                {xTickLabel(point)}
                            </text>
                        </g>
                    ))}
                    {!isMultiSeries && <polygon className='standard-line-chart-area' fill={`url(#${gradientId})`} points={areaPoints} />}
                    {seriesLines.map((serie) => (
                        <polyline
                            className='standard-line-chart-line'
                            key={`line-${serie.key}`}
                            points={serie.linePoints}
                            style={{ '--series-color': serie.color, strokeDasharray: serie.dashed ? '8 7' : undefined }}
                        />
                    ))}
                    {points.map((point) => (
                        <g key={pointKey(point)}>
                            <line
                                className={`standard-line-chart-hover-line ${isActivePoint(point) ? 'is-active' : ''}`}
                                x1={point.x}
                                x2={point.x}
                                y1={padding.top}
                                y2={height - padding.bottom}
                            />
                            {hasCircleHitArea
                                ? (
                                    <circle
                                        aria-label={pointAriaLabel(point)}
                                        className={`standard-line-chart-hit-area ${isActivePoint(point) ? 'is-active' : ''}`}
                                        cx={point.x}
                                        cy={point.y}
                                        r='14'
                                        tabIndex='0'
                                        onBlur={() => setActivePoint(null)}
                                        onFocus={() => setActivePoint(point)}
                                        onMouseEnter={() => setActivePoint(point)}
                                    />
                                )
                                : (
                                    <rect
                                        aria-label={pointAriaLabel(point)}
                                        className={`standard-line-chart-hit-area ${isActivePoint(point) ? 'is-active' : ''}`}
                                        x={point.x - hitAreaWidth / 2}
                                        y={padding.top}
                                        width={hitAreaWidth}
                                        height={chartHeight}
                                        tabIndex='0'
                                        onBlur={() => setActivePoint(null)}
                                        onFocus={() => setActivePoint(point)}
                                        onMouseEnter={() => setActivePoint(point)}
                                    />
                                )}
                            {(hasVisiblePoints || isActivePoint(point)) && chartSeries.map((serie, serieIndex) => (
                                <circle
                                    className={`standard-line-chart-point ${isActivePoint(point) ? 'is-active' : ''}`}
                                    cx={point.x}
                                    cy={point.values[serieIndex].y}
                                    key={`point-${serie.key}`}
                                    r='4'
                                    style={{ '--series-color': serie.color }}
                                />
                            ))}
                        </g>
                    ))}
                </svg>
                {activePoint && (
                    <div className='standard-line-chart-tooltip' style={activeTooltipStyle}>
                        <span>{activePoint.label}</span>
                        {chartSeries.map((serie) => (
                            <strong key={`tooltip-${serie.key}`} style={{ '--series-color': serie.color }}>
                                {isMultiSeries && <i />}
                                {serie.tooltipValue(activePoint)}
                            </strong>
                        ))}
                    </div>
                )}
                {points.length > 0 && (
                    <div className='standard-line-chart-scale'>
                        {seriesLines.map((serie) => (
                            <strong key={`peak-${serie.key}`}>
                                {serie.peakText(serie.peak)}
                            </strong>
                        ))}
                    </div>
                )}
            </div>
        </Paper>
    );
};

export default LineChart;
